import { RefreshCw } from 'lucide-react';
import React from 'react';

import { useWebsiteBreakdown, useWebsiteHealth } from '@/features/dashboard/use-dashboard';
import { BreakdownSection } from '@/features/website-dashboard/components/BreakdownSection';
import { SiteHealthKpis } from '@/features/website-dashboard/components/SiteHealthKpis';
import { SurfaceTiles } from '@/features/website-dashboard/components/SurfaceTiles';
import { Worklist } from '@/features/website-dashboard/components/Worklist';
import {
  deriveCompleteness,
  deriveGaps,
  deriveHealth,
  TEAL,
} from '@/features/website-dashboard/lib';

/**
 * Admin-only Website Dashboard. A standalone read of everything on the public
 * site - blogs, pages, destination / category landing pages and packages - and
 * what is still missing from it. Every number is real: the headline KPIs, the
 * surface tiles, the worklist and the breakdown tables all read from the two
 * existing aggregates (`useWebsiteHealth`, `useWebsiteBreakdown`), both of
 * which already allow ADMIN.
 *
 * The page matches the supplied Claude Design mockup (Manrope, #f5f6f6 canvas,
 * teal accent). The font + table styling are injected scoped to `.wd-root` so
 * they do not touch the rest of the admin shell.
 */

/** Manrope is loaded the same way the app loads Sofia Sans - a Google Fonts link. */
const MANROPE_HREF =
  'https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&display=swap';

const useManropeFont = (): void => {
  React.useEffect(() => {
    if (document.querySelector(`link[data-wd-font]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = MANROPE_HREF;
    link.setAttribute('data-wd-font', 'true');
    document.head.appendChild(link);
  }, []);
};

const SCOPED_CSS = `
.wd-root{font-family:Manrope,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;color:#17171a;font-variant-numeric:tabular-nums}
.wd-root a{color:${TEAL};text-decoration:none}
.wd-root a:hover{color:#0a2f3d;text-decoration:underline}
.wd-root table{border-collapse:separate;border-spacing:0;width:100%}
.wd-root th{position:sticky;top:0;z-index:1;background:#f8f9f9;border-bottom:1px solid #e3e5e5;padding:9px 12px;font-size:12px;font-weight:600;color:#6b7075;text-align:center;white-space:nowrap}
.wd-root th:first-child{text-align:left}
.wd-root td{border-bottom:1px solid #eef0f0;padding:10px 12px;font-size:13px;text-align:center;white-space:nowrap}
.wd-root td:first-child{text-align:left;font-weight:600;color:#17171a}
.wd-root tr:hover td{background:#fafbfb}
.wd-root tfoot td{background:#f8f9f9;font-weight:700;border-top:1px solid #e3e5e5;border-bottom:0;position:sticky;bottom:0}
.wd-kpis{grid-template-columns:repeat(4,minmax(0,1fr))}
.wd-tiles{grid-template-columns:repeat(5,minmax(0,1fr))}
.wd-work{grid-template-columns:repeat(3,minmax(0,1fr))}
.wd-three{grid-template-columns:repeat(3,minmax(0,1fr))}
.wd-two{grid-template-columns:repeat(2,minmax(0,1fr))}
.wd-pages{grid-template-columns:minmax(0,1fr) minmax(0,2fr)}
@media (max-width:1100px){.wd-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.wd-tiles{grid-template-columns:repeat(3,minmax(0,1fr))}.wd-work{grid-template-columns:1fr}}
@media (max-width:1000px){.wd-three{grid-template-columns:1fr}.wd-two{grid-template-columns:1fr}.wd-pages{grid-template-columns:1fr}}
@media (max-width:600px){.wd-tiles{grid-template-columns:repeat(2,minmax(0,1fr))}}
`;

const formatGenerated = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Centered = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div style={{ padding: '80px 24px', textAlign: 'center', fontSize: 13, color: '#6b7075' }}>
    {children}
  </div>
);

export const WebsiteDashboardPage = (): React.JSX.Element => {
  useManropeFont();
  const health = useWebsiteHealth();
  const breakdown = useWebsiteBreakdown();

  const refreshing = health.isFetching || breakdown.isFetching;
  const refresh = (): void => {
    void health.refetch();
    void breakdown.refetch();
  };

  const healthData = health.data;
  const breakdownData = breakdown.data;

  const model = React.useMemo(() => {
    if (!healthData) return null;
    const completeness = deriveCompleteness(breakdownData?.destinationPageCompleteness ?? []);
    return {
      health: deriveHealth(healthData, breakdownData),
      completeness,
      gaps: breakdownData ? deriveGaps(breakdownData, completeness) : null,
    };
  }, [healthData, breakdownData]);

  return (
    <div className="wd-root" style={{ background: '#f5f6f6', minHeight: '100%' }}>
      <style>{SCOPED_CSS}</style>
      <div
        style={{
          maxWidth: 1360,
          margin: '0 auto',
          padding: '24px 16px 56px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: '#6b7075',
              }}
            >
              Admin · Website
            </div> */}
            <h1
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 800,
                letterSpacing: '-.03em',
                lineHeight: 1.1,
              }}
            >
              Website dashboard
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7075' }}>
              Everything on the public site - blogs, pages, destination and category landing pages,
              packages - and what is still missing from it.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {healthData?.generatedAt && (
              <span style={{ fontSize: 11, color: '#8a9095', whiteSpace: 'nowrap' }}>
                Generated {formatGenerated(healthData.generatedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              title="Refresh"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                border: '1px solid #e3e5e5',
                borderRadius: 10,
                background: '#fff',
                color: '#55595e',
                cursor: refreshing ? 'default' : 'pointer',
              }}
            >
              <RefreshCw
                style={{
                  width: 15,
                  height: 15,
                  animation: refreshing ? 'wd-spin 1s linear infinite' : undefined,
                }}
              />
            </button>
          </div>
        </div>

        <style>{`@keyframes wd-spin{to{transform:rotate(360deg)}}`}</style>

        {health.isError ? (
          <Centered>Could not load the website dashboard. Try refreshing.</Centered>
        ) : !model ? (
          <Centered>Loading the website dashboard…</Centered>
        ) : (
          <>
            <SiteHealthKpis
              health={model.health}
              completeness={model.completeness}
              gaps={model.gaps}
            />
            <SurfaceTiles health={healthData!} />
            <Worklist health={healthData!} breakdown={breakdownData} />
            {breakdown.isError ? (
              <Centered>Could not load the detailed breakdown.</Centered>
            ) : breakdownData ? (
              <BreakdownSection data={breakdownData} />
            ) : (
              <Centered>Loading the detailed breakdown…</Centered>
            )}
          </>
        )}
      </div>
    </div>
  );
};
